import React from 'react';
import { ProjectOverview as ProjectOverviewType } from '../types';
import Icon from '../../../components/AppIcon';

interface ProjectOverviewProps {
  overview: ProjectOverviewType;
}

const ProjectOverview = ({ overview }: ProjectOverviewProps) => {
  const features = overview.keyFeatures
    ? overview.keyFeatures.split('\n').filter(f => f.trim())
    : [];

  return (
    <section className="mb-24">
      <h2 className="text-3xl font-black text-[#0C2C39] mb-12 tracking-tight uppercase">
        Project <span className="text-[#0D9488]">Overview</span>
      </h2>

      {/* Unified Main Card */}
      <div className="bg-white border-2 border-[#0C2C39] rounded-[2.5rem] shadow-[12px_12px_0px_0px_#0C2C39] overflow-hidden">
        <div className="p-8 lg:p-12 space-y-12">

          {/* Header Section: Subtitle & About */}
          <div className="space-y-6">
            {overview.subtitle && (
              <h3 className="text-2xl md:text-3xl font-black text-[#0C2C39] uppercase tracking-tighter leading-none">
                {overview.subtitle}
              </h3>
            )}

            {overview.description && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0D9488]">The Vision</span>
                  <div className="h-px flex-1 bg-[#0C2C39]/10"></div>
                </div>
                <p className="text-[#0C2C39]/70 leading-relaxed font-medium text-lg">
                  {overview.description}
                </p>
              </div>
            )}
          </div>

          {/* Core Content: Challenge & Solution */}
          {(overview.challenge || overview.solution) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              {overview.challenge && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center">
                      <Icon name="AlertCircle" size={16} className="text-red-500" />
                    </div>
                    <h4 className="text-sm font-black text-[#0C2C39] uppercase tracking-wider">The Challenge</h4>
                  </div>
                  <p className="text-[#0C2C39]/60 leading-relaxed font-medium text-[15px]">
                    {overview.challenge}
                  </p>
                </div>
              )}

              {overview.solution && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0D9488]/10 rounded-lg flex items-center justify-center">
                      <Icon name="Lightbulb" size={16} className="text-[#0D9488]" />
                    </div>
                    <h4 className="text-sm font-black text-[#0C2C39] uppercase tracking-wider">The Solution</h4>
                  </div>
                  <p className="text-[#0C2C39]/60 leading-relaxed font-medium text-[15px]">
                    {overview.solution}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Dynamic Sections: Features & Meta */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 pt-8 border-t border-[#0C2C39]/5">

            {/* Left: Key Features (2/3 width on large screens if possible, here 2 columns) */}
            {features.length > 0 && (
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#0D9488] rounded-lg flex items-center justify-center text-white">
                    <Icon name="Zap" size={16} />
                  </div>
                  <h4 className="text-sm font-black text-[#0C2C39] uppercase tracking-wider">Strategic Features</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 group">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#0D9488] flex-shrink-0"></div>
                      <span className="text-[#0C2C39]/70 font-bold leading-tight text-sm uppercase tracking-wide">{feature.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Right: Meta Info (Role & Achievement) */}
            <div className="space-y-8">
              {overview.myRole && (
                <div className="space-y-3">
                  <p className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">Contribution</p>
                  <p className="text-[#0C2C39] font-black text-xl leading-tight">
                    {overview.myRole}
                  </p>
                </div>
              )}

              {overview.achievement && (
                <div className="bg-[#FFF9EB] border border-[#0C2C39]/10 rounded-2xl p-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Award" size={14} className="text-[#0D9488]" />
                    <span className="text-[10px] font-black text-[#0D9488] uppercase tracking-[0.2em]">Key Milestone</span>
                  </div>
                  <p className="text-[#0C2C39]/70 leading-relaxed font-bold text-sm">
                    {overview.achievement}
                  </p>
                </div>
              )}

              {overview.targetAudience && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">End User</p>
                  <p className="text-[#0C2C39]/60 font-medium text-sm italic">
                    {overview.targetAudience}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectOverview;